import React, { useContext, useState, useEffect, useRef, createContext } from 'react';
import { Box, Typography, Grid, TextField, Paper, Card, CardContent, Alert, Avatar, FormControl, InputLabel, Select, MenuItem, CircularProgress, Chip, Divider} from '@mui/material';
import { WhatsApp, CheckCircle, ErrorOutline, Message, Watch } from '@mui/icons-material';
import { NotificationDataContext } from '../../AddEditNotification';
import { NotificationServices } from '../../NotificationServices';
import RenderTemplatePreview from './RenderTemplatePreview';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import MessagePreview from './whatsapp/MessagePreview';
import MessageTypeSelection from './whatsapp/MessageTypeSelection';
import TemplateSelection from './whatsapp/TemplateSelection';
import TemplateContent from './whatsapp/TemplateContent';
import TextMessageContent from './TextMessageContent';
import TextContent from './whatsapp/TextContent';
import NavigationButton from '../NavigationButton';
import MediaContent from './whatsapp/MediaContent';

const WhatsappDataContext = createContext();
const WhatsappMessageContent = () => {
    const { selectedTemplate, setSelectedTemplate,notificationData, setNotificationData,initialState,steps,activeStep,setActiveStep,completedSteps, setCompletedSteps } = useContext(NotificationDataContext);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [restoringData, setRestoringData] = useState(false);
    const isRestoringRef = useRef(false);
    const initialLoadRef = useRef(true);
    const initialWhatsappData = {
            messageType:'text',
            message:'',
            templateName: '',
            languageCode: 'en',
            components: [],
            mediaType: '',// image, video, document, audio
            mediaUrl: '',
            caption: '',
            whatsappTemplateParams: {},
        };
    const WhatsappFormMethod = useForm({
        mode:'onChange',
        defaultValues: initialWhatsappData
    });

    const {control,watch,reset,setValue,formState:{isDirty},handleSubmit} = WhatsappFormMethod;
    const messageType = watch('messageType');
    const template = watch('templateName');

    const completeStep = (data) => {
        console.log('whatsapp content Data:', data);
        const payload = buildPayload(data);
        console.log('WhatsApp Payload:', payload);
        
        // Save form data to notificationData
        setNotificationData(prev => ({
            ...prev,
            whatsappData: {
                ...payload,
                // Store all form data for restoration
                formData: data,
                // Store selected template for restoration
                selectedTemplateData: selectedTemplate
            }
        }));
        
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setCompletedSteps((prev) => {
                prev.add(activeStep);
                return new Set(prev);
            });
        }
    }

    // Map stored WhatsApp components back to form param keys
    const mapComponentsToParams = (components) => {
        const params = {};
        (components || []).forEach((comp) => {
            if (comp.type === 'body' && Array.isArray(comp.parameters)) {
                comp.parameters.forEach((p, idx) => {
                    params[`body_${idx + 1}`] = p?.text || '';
                });
            }
            if (comp.type === 'header' && Array.isArray(comp.parameters)) {
                comp.parameters.forEach((p, idx) => {
                    params[`header_${idx + 1}`] = p?.text || '';
                });
            }
        });
        return params;
    };

    

    const fetchWhatsAppTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Call your API to fetch WhatsApp Cloud API templates
            const response = await NotificationServices.fetchWhatsAppTemplates();
            
            if (response.success == "1" && response.data) {
                setTemplates(response.data);
            } else {
                setError(response.message || 'Failed to fetch templates');
            }
        } catch (err) {
            console.error('Error fetching WhatsApp templates:', err);
            setError('Failed to load WhatsApp templates. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateChange = () => {
        // const templateId = event.target.value;
        const templateObj = templates.find(t => t.name === template);
        
        setSelectedTemplate(templateObj);
        setValue('languageCode', templateObj?.language);
        
        // Initialize template parameters with empty values
        const initialParams = {};
        if (templateObj?.components) {
            templateObj.components.forEach(component => {
                if (component.type === 'BODY' && component.parameters) {
                    component.parameters.forEach((param, index) => {
                        initialParams[`body_${index + 1}`] = '';
                    });
                }
                if (component.type === 'HEADER' && component.parameters) {
                    component.parameters.forEach((param, index) => {
                        initialParams[`header_${index + 1}`] = '';
                    });
                }
            });
        }

        // Preserve any existing filled params while ensuring keys exist
        const prevParams = watch('whatsappTemplateParams') || {};
        const merged = { ...initialParams, ...prevParams };
        console.log('Merged Template Params:', merged);
        setValue('whatsappTemplateParams', merged, { shouldDirty: false });
        console.log("form values after handleTemplateChange:",watch());
    };

    

    const extractTemplateVariables = (template) => {
        const variables = [];
        
        if (!template?.components) return variables;
        
        template.components.forEach(component => {
            if (component.type === 'BODY' && component.text) {
                // Extract {{1}}, {{2}}, etc. from template text
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach(match => {
                        const index = match.replace(/[{}]/g, '');
                        variables.push({
                            key: `body_${index}`,
                            label: `Body Parameter ${index}`,
                            type: 'text',
                            example: component.example?.body_text?.[0]?.[parseInt(index) - 1] || ''
                        });
                    });
                }
            }
            
            if (component.type === 'HEADER' && component.format === 'TEXT' && component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach(match => {
                        const index = match.replace(/[{}]/g, '');
                        variables.push({
                            key: `header_${index}`,
                            label: `Header Parameter ${index}`,
                            type: 'text',
                            example: component.example?.header_text?.[parseInt(index) - 1] || ''
                        });
                    });
                }
            }
        });
        
        return variables;
    };

    const templateVariables = selectedTemplate ? extractTemplateVariables(selectedTemplate) : [];
    // Build WhatsApp API-compatible payloads from form state
    const buildTemplatePayload = (formValues) => {
        if (!selectedTemplate) return null;
        const lang = selectedTemplate.language || formValues.languageCode || 'en_US';

        const params = formValues.whatsappTemplateParams || {};
        const bodyParamEntries = Object.keys(params)
            .filter(k => k.startsWith('body_'))
            .map(k => ({ key: k, index: parseInt(k.split('_')[1], 10), value: params[k] }))
            .sort((a, b) => a.index - b.index)
            .map(entry => ({ type: 'text', text: entry.value }));

        const headerParamEntries = Object.keys(params)
            .filter(k => k.startsWith('header_'))
            .map(k => ({ key: k, index: parseInt(k.split('_')[1], 10), value: params[k] }))
            .sort((a, b) => a.index - b.index)
            .map(entry => ({ type: 'text', text: entry.value }));

        const components = [];
        if (headerParamEntries.length > 0) {
            components.push({ type: 'header', parameters: headerParamEntries });
        }
        if (bodyParamEntries.length > 0) {
            components.push({ type: 'body', parameters: bodyParamEntries });
        }

        return {
            templateName: selectedTemplate.name,
            languageCode: lang,
            components,
            messageType: 'template'
        };
    };

    const buildPayload = (formValues) => {
        if (messageType === 'template') {
            const templatePayload = buildTemplatePayload(formValues);
            return {
                ...templatePayload,
                // Include original form values for restoration
                whatsappTemplateParams: formValues.whatsappTemplateParams
            };
        }
        if (messageType === 'text') {
            return {
                messageType: 'text',
                message: formValues.message
            };
        }
        if (messageType === 'media') {
            return {
                messageType: 'media',
                mediaType: formValues.mediaType,
                mediaUrl: formValues.mediaUrl,
                caption: formValues.caption
            };
        }
        // Placeholder for 'button' and 'list' implementations
        return null;
    };
    // Restore form data when coming back to this step
    useEffect(() => {
        const savedWhatsappData = notificationData.whatsappData;
        
        if (savedWhatsappData && savedWhatsappData.formData) {
            console.log('Restoring WhatsApp form data:', savedWhatsappData.formData);
            isRestoringRef.current = true;
            setRestoringData(true);
            
            // Fetch templates first if message type is template
            if (savedWhatsappData.formData.messageType === 'template') {
                fetchWhatsAppTemplates().then(() => {
                    // Restore all form fields after templates are loaded
                    Object.keys(savedWhatsappData.formData).forEach(key => {
                        setValue(key, savedWhatsappData.formData[key], { shouldDirty: false });
                    });
                    
                    // Restore selected template
                    if (savedWhatsappData.selectedTemplateData) {
                        setSelectedTemplate(savedWhatsappData.selectedTemplateData);
                    }
                    
                    setTimeout(() => {
                        isRestoringRef.current = false;
                        initialLoadRef.current = false;
                        setRestoringData(false);
                    }, 100);
                });
            } else {
                // For non-template types, restore directly
                Object.keys(savedWhatsappData.formData).forEach(key => {
                    setValue(key, savedWhatsappData.formData[key], { shouldDirty: false });
                });
                
                setTimeout(() => {
                    isRestoringRef.current = false;
                    initialLoadRef.current = false;
                    setRestoringData(false);
                }, 100);
            }
        } else {
            initialLoadRef.current = false;
        }
    }, []);

    // Reset form when message type changes to clear previous message type data
    useEffect(() => {
        // Skip on initial mount and during restoration
        if (initialLoadRef.current || isRestoringRef.current) {
            return;
        }
        
        if (messageType) {
            console.log('Message type changed to:', messageType);
            
            const savedWhatsappData = notificationData.whatsappData;
            
            // Check if saved data exists and matches the selected message type
            if (savedWhatsappData && savedWhatsappData.messageType === messageType) {
                console.log('Restoring saved data for message type:', messageType);
                
                // Restore saved data for this message type
                if (savedWhatsappData.formData) {
                    Object.keys(savedWhatsappData.formData).forEach(key => {
                        setValue(key, savedWhatsappData.formData[key], { shouldDirty: false });
                    });
                    
                    // Restore selected template if it's a template message
                    if (messageType === 'template' && savedWhatsappData.selectedTemplateData) {
                        setSelectedTemplate(savedWhatsappData.selectedTemplateData);
                        // Fetch templates if not already loaded
                        if (templates.length === 0) {
                            fetchWhatsAppTemplates();
                        }
                    }
                }
            } else {
                // No saved data for this message type, reset to initial state
                console.log('No saved data for message type, resetting to initial state');
                
                const resetData = {
                    ...initialWhatsappData,
                    messageType: messageType
                };
                
                reset(resetData);
                setSelectedTemplate(null);
                
                // Fetch templates if switching to template mode
                if (messageType === 'template') {
                    fetchWhatsAppTemplates();
                }
            }
        }
    }, [messageType]);
    
    // Handle template selection and restoration
    useEffect(() => {
        if (template && templates.length > 0 && !isRestoringRef.current) {
            handleTemplateChange();
        }
    }, [template, templates]);
    return (
        <WhatsappDataContext.Provider value={{loading,initialWhatsappData,templates, selectedTemplate, handleTemplateChange, templateVariables,restoringData}}>
            <FormProvider {...WhatsappFormMethod}>
                <Box component={'form'} onSubmit={handleSubmit(completeStep)}>
                    {/* <Typography gutterBottom sx={{ mb: 2, fontWeight: 600, fontSize: '0.85rem' }}>
                        WhatsApp Message Configuration
                    </Typography> */}

                   
                    <Grid container spacing={3} >
                                {/* Template Selection & Parameters */}
                                <Grid item xs={12} md={8} sx={{height:'59vh',overflow:'auto'}}>
                                     {restoringData ? (
                                            <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
                                                <CircularProgress />
                                                <Typography variant="body2" color="text.secondary">
                                                    Loading WhatsApp message data...
                                                </Typography>
                                            </Card>
                                        ) : (
                                            <>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <MessageTypeSelection/>
                                                            </Grid>
                                                            {messageType == 'template' && <TemplateSelection/>}
                                                        </Grid>
                                                        {(messageType == 'template' && template) && <TemplateContent/>}
                                                        {(messageType == 'text') && <TextContent/>}
                                                        {(messageType == 'media') && <MediaContent/>}
                                                    </CardContent>
                                                </Card>
                                            </>
                                        )}
                                    
                                </Grid>

                                {/* Preview */}
                                <Grid item xs={12} md={4}  sx={{height:'59vh',overflow:'auto'}}>
                                    {restoringData ? (
                                            <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
                                                <CircularProgress />
                                                <Typography variant="body2" color="text.secondary">
                                                    Loading WhatsApp message Preview...
                                                </Typography>
                                            </Card>
                                        ) : (
                                    <MessagePreview/>
                                        )}
                                </Grid>
                            </Grid>
                            
                            <NavigationButton isDirty={isDirty}/>
                </Box>
            </FormProvider>
        </WhatsappDataContext.Provider>
    );
};

export default WhatsappMessageContent;
export { WhatsappDataContext };
